import { Trans } from '@lingui/macro'
import {
    Analytics as AnalyticsIcon,
    CloudQueue as CloudIcon,
    Dataset as DatasetIcon,
    Group as GroupIcon,
    InsertChart as InsertChartIcon,
    LibraryBooks as LibraryBooksIcon,
    Search as SearchIcon
} from '@mui/icons-material'
import { Box, Button, Card, CardContent, Grid, Paper, Typography } from '@mui/material'
import { PlatformConfigState } from '@om1/platform-config/state'
import { Routes, toPath } from '@om1/platform-routing'
import { FrameworkComponentProps } from '@om1/platform-utils'
import React from 'react'
import { useHistory } from 'react-router-dom'

export type HomeProfileComponentProps = FrameworkComponentProps<PlatformConfigState, {}, {}>

interface ApplicationCardProps {
    title: React.ReactNode
    description: React.ReactNode
    icon: React.ReactNode
    route: string
    onNavigate: (route: string) => void
}

const ApplicationCard: React.FC<ApplicationCardProps> = ({ title, description, icon, route, onNavigate }) => (
    <Card
        sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
            '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: 3
            },
            cursor: 'pointer'
        }}
        onClick={() => onNavigate(route)}
    >
        <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                {icon}
                <Typography variant='h6' component='h3' sx={{ ml: 1, fontWeight: 600 }}>
                    {title}
                </Typography>
            </Box>
            <Typography variant='body2' color='text.secondary' sx={{ flexGrow: 1, mb: 2 }}>
                {description}
            </Typography>
            <Button
                variant='outlined'
                size='small'
                sx={{ alignSelf: 'flex-start', mt: 'auto' }}
                endIcon={
                    <Box component='span' sx={{ ml: 0.5 }}>
                        →
                    </Box>
                }
            >
                <Trans>Open</Trans>
            </Button>
        </CardContent>
    </Card>
)

interface ResourceCardProps {
    title: React.ReactNode
    description: React.ReactNode
    icon: React.ReactNode
    onNavigate: () => void
}

const ResourceCard: React.FC<ResourceCardProps> = ({ title, description, icon, onNavigate }) => (
    <Box
        sx={{
            textAlign: 'center',
            p: 1,
            cursor: 'pointer',
            transition: 'background-color 0.2s ease-in-out',
            borderRadius: 1,
            '&:hover': {
                backgroundColor: 'action.hover'
            }
        }}
        onClick={onNavigate}
    >
        <Box sx={{ mb: 1, display: 'flex', justifyContent: 'center' }}>{icon}</Box>
        <Typography variant='h6' component='h3' sx={{ fontWeight: 600, mb: 1 }}>
            {title}
        </Typography>
        <Typography variant='body2' color='text.secondary' sx={{ lineHeight: 1.4 }}>
            {description}
        </Typography>
    </Box>
)

export const HomePageProfileComponent: React.FunctionComponent<HomeProfileComponentProps> = ({ state }) => {
    //const { user } = useUserPermissions()
    const history = useHistory()

    const handleNavigate = (route: string) => {
        history.push(toPath(route as Routes))
    }

    const applications = [
        {
            title: <Trans>View Datasets</Trans>,
            description: <Trans>View highly-curated datasets built by OM1</Trans>,
            icon: <DatasetIcon sx={{ color: 'primary.main', fontSize: 28 }} />,
            route: Routes.COHORTBUILD_DATASET
        },
        {
            title: <Trans>Explore Cohorts</Trans>,
            description: <Trans>Build and analyze custom patient cohorts</Trans>,
            icon: <GroupIcon sx={{ color: 'primary.main', fontSize: 28 }} />,
            route: Routes.COHORTBUILD_CUSTOM
        },
        {
            title: <Trans>Generate Insights</Trans>,
            description: <Trans>Generate comprehensive analytics and insights</Trans>,
            icon: <AnalyticsIcon sx={{ color: 'primary.main', fontSize: 28 }} />,
            route: Routes.INSIGHTS_LIBRARY
        }
    ]

    const resources = [
        {
            title: <Trans>Insights Platform</Trans>,
            description: <Trans>Learn about the capabilities of the platform</Trans>,
            icon: <InsertChartIcon sx={{ color: 'primary.main', fontSize: 32 }} />,
            section: 'platform',
            subsection: 'getting-started'
        },
        {
            title: <Trans>Real World Data Cloud</Trans>,
            description: <Trans>Learn about data available within the Real World Data Cloud (RWDC)</Trans>,
            icon: <CloudIcon sx={{ color: 'primary.main', fontSize: 32 }} />,
            section: 'rwdc',
            subsection: 'getting-started'
        },
        {
            title: <Trans>Cohort Explorer</Trans>,
            description: <Trans>Build and analyze custom patient cohorts</Trans>,
            icon: <SearchIcon sx={{ color: 'primary.main', fontSize: 32 }} />,
            section: 'platform',
            subsection: 'cohort-explorer'
        },
        {
            title: <Trans>Insights Library</Trans>,
            description: <Trans>Comprehensive analytics and reporting suite</Trans>,
            icon: <LibraryBooksIcon sx={{ color: 'primary.main', fontSize: 32 }} />,
            section: 'platform',
            subsection: 'insights-library'
        }
    ]

    const handleDocumentationNavigate = (section: string, subsection: string) => {
        const url = `${toPath(Routes.DOCUMENTATION)}?section=${section}&subsection=${subsection}`
        history.push(url)
    }

    return (
        <Box sx={{ p: 0 }}>
            {/* Welcome Header */}
            <Box sx={{ mb: 4, mt: 2 }}>
                <Typography variant='h4' component='h1' sx={{ fontWeight: 600, mb: 1 }}>
                    <Trans>Welcome to the OM1 Insights Platform</Trans>
                </Typography>
            </Box>

            {/* Applications Section */}
            <Box sx={{ mb: 4 }}>
                <Typography variant='h5' component='h2' sx={{ fontWeight: 600, mb: 3 }}>
                    <Trans>Applications</Trans>
                </Typography>
                <Grid container spacing={3}>
                    {applications.map((app, index) => (
                        <Grid item xs={12} md={4} key={index}>
                            <ApplicationCard
                                title={app.title}
                                description={app.description}
                                icon={app.icon}
                                route={app.route}
                                onNavigate={handleNavigate}
                            />
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* Getting Started Section */}
            <Box sx={{ mt: 6 }}>
                <Typography variant='h5' component='h2' sx={{ fontWeight: 600, mb: 1 }}>
                    <Trans>Getting Started</Trans>
                </Typography>
                <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
                    <Trans>New to the Insights Platform? Here are some helpful resources to get you started.</Trans>
                </Typography>
                <Paper sx={{ p: 3 }}>
                    <Grid container spacing={3} sx={{ justifyContent: 'space-between' }}>
                        {resources.map((resource, index) => (
                            <Grid item xs={12} sm={6} md={3} key={index}>
                                <ResourceCard
                                    title={resource.title}
                                    description={resource.description}
                                    icon={resource.icon}
                                    onNavigate={() => handleDocumentationNavigate(resource.section, resource.subsection)}
                                />
                            </Grid>
                        ))}
                    </Grid>
                </Paper>
            </Box>
        </Box>
    )
}
